import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Navigation, Pagination, Autoplay, EffectFade } from 'swiper/modules';
import { useCounter } from '../hooks';
import { getCounterData, getTargetNumbers, formatNumber } from '../data/counterData';
import Footer from '../components/Footer';

// Fancybox imports
import { Fancybox } from "@fancyapps/ui";
import "@fancyapps/ui/dist/fancybox/fancybox.css";

// Swiper CSS'lerini import ediyoruz
import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/pagination';
import 'swiper/css/effect-fade';

function Home({ siteData }) {
  
  const counterData = getCounterData(siteData);
  const targetNumbers = {};
  Object.keys(counterData).forEach(key => {
    targetNumbers[key] = counterData[key].target;
  });
  const { counters, counterRef } = useCounter(targetNumbers, 3000);
  const [activeFaq, setActiveFaq] = useState(0);

  // Fancybox'ı initialize et
  useEffect(() => {
    Fancybox.bind("[data-fancybox]", {
      // Minimal konfigürasyon
      Toolbar: {
        display: {
          left: ["infobar"],
          middle: [],
          right: ["close"],
        },
      },
    });

    return () => {
      Fancybox.destroy();
    };
  }, []);

  const toggleFaq = (index) => {
    setActiveFaq(activeFaq === index ? null : index);
  };

  const openVideo = (videoUrl) => {
    console.log("Video URL:", videoUrl);
    Fancybox.show([
      {
        src: videoUrl,
      },
    ]);
  };

  // HTML entity decode fonksiyonu
  const decodeHtml = (html) => {
    if (!html) return '';
    if (typeof window === 'undefined') {
      return html;
    }

    const textarea = document.createElement('textarea');
    textarea.innerHTML = html;
    const decoded = textarea.value;

    // Bir kez decode ettikten sonra hala entity varsa bir tur daha decode et
    if (decoded !== html && /&[a-zA-Z]+;|&#\d+;/.test(decoded)) {
      textarea.innerHTML = decoded;
      return textarea.value;
    }

    return decoded;
  };

  const mapDescriptionHtml = siteData?.siteSettings?.map_description
    ? decodeHtml(siteData.siteSettings.map_description)
    : '';

  if (!siteData) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '100vh',
        fontSize: '18px',
        color: '#666'
      }}>
        <p>Yükleniyor...</p>
      </div>
    );
  }

  return (
    <div style={{ minHeight: '100vh' }}>
      <Helmet>
        <title>1 Araç 10 Ağaç - Ağaçlandırma Seferberliği | ALJ Türkiye</title>
        <meta 
          name="description" 
          content="ALJ Türkiye, “1 Araç 10 Ağaç“ ağaç dikim kampanyasını başlatıyor. Ağaçlandırma seferberliği ile sürdürülebilir bir gelecek için adım atıyoruz." 
        />
        <meta 
          name="keywords" 
          content="Toyota, ağaçlandırma, çevre, sürdürülebilirlik, doğa, yeşil gelecek, fidan, orman" 
        />
        <meta property="og:title" content="1 Araç 10 Ağaç - Ağaçlandırma Seferberliği | ALJ Türkiye" />
        <meta 
          property="og:description" 
          content="ALJ Türkiye, “1 Araç 10 Ağaç“ ağaç dikim kampanyasını başlatıyor. Ağaçlandırma seferberliği ile sürdürülebilir bir gelecek için adım atıyoruz." 
        />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={window.location.href} />
        <meta property="og:image" content="http://agaclandirmaseferberligi.com/api/storage/app/media/sliders/1000x800.jpg" />
        <meta property="og:locale" content="tr_TR" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="1 Araç 10 Ağaç - Ağaçlandırma Seferberliği | ALJ Türkiye" />
        <meta 
          name="twitter:description" 
          content="ALJ Türkiye, “1 Araç 10 Ağaç“ ağaç dikim kampanyasını başlatıyor. Ağaçlandırma seferberliği ile sürdürülebilir bir gelecek için adım atıyoruz." 
        />
        <meta name="twitter:image" content="http://agaclandirmaseferberligi.com/api/storage/app/media/sliders/1000x800.jpg" />
        <link rel="canonical" href={window.location.origin} />
        <meta name="robots" content="index, follow" />
        <meta name="author" content="ALJ Türkiye" />
        
        {/* Slider fotoğrafları için preload linkleri - Site hızı ve PageSpeed için */}
        {siteData.sliders && siteData.sliders.map((slide, index) => (
          <React.Fragment key={`preload-${slide.id}`}>
            <link 
              rel="preload" 
              as="image" 
              href={slide.image_desktop} 
              fetchpriority={index === 0 ? "high" : "low"}
            />
            <link 
              rel="preload" 
              as="image" 
              href={slide.image_mobile} 
              fetchpriority={index === 0 ? "high" : "low"}
            />
          </React.Fragment>
        ))}
        
        {/* JSON-LD Structured Data - Organization Schema */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Organization",
            "name": "ALJ Türkiye Ağaçlandırma Seferberliği",
            "url": "https://agaclandirmaseferberligi.com",
            "logo": "http://agaclandirmaseferberligi.com/api/storage/app/media/sliders/1000x800.jpg",
            "sameAs": [
              "https://www.linkedin.com/company/aljt%C3%BCrkiye/",
              "https://www.instagram.com/aljturkiye/",
              "https://www.facebook.com/aljturkiye"
            ],
            "description": "ALJ Türkiye'nin Toyota, Lexus ve BYD markaları aracılığıyla başlattığı çevre projesi: 1 Araç 10 Ağaç - Ağaçlandırma Seferberliği.",
            "sponsor": [
              {
                "@type": "Organization",
                "name": "Castrol",
                "url": "https://www.castrol.com/tr_tr/turkey/home.html"
              },
              {
                "@type": "Organization",
                "name": "Reysaş",
                "url": "https://reysas.com/"
              }
            ]
          })}
        </script>

        {/* JSON-LD Structured Data - WebPage Schema */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "WebPage",
            "name": "1 Araç 10 Ağaç - Ağaçlandırma Seferberliği",
            "url": "https://agaclandirmaseferberligi.com",
            "description": "ALJ Türkiye'nin başlattığı çevre projesi: 1 Araç 10 Ağaç - Ağaçlandırma Seferberliği. Toyota, Lexus ve BYD markaları aracılığıyla sürdürülebilir bir gelecek için ağaçlandırma hareketi.",
            "inLanguage": "tr",
            "publisher": {
              "@type": "Organization",
              "name": "ALJ Türkiye",
              "url": "https://agaclandirmaseferberligi.com",
              "logo": {
                "@type": "ImageObject",
                "url": "http://agaclandirmaseferberligi.com/api/storage/app/media/sliders/1000x800.jpg"
              }
            }
          })}
        </script>

        {/* JSON-LD Structured Data - FAQPage Schema */}
        {siteData.faqs && siteData.faqs.length > 0 && (
          <script type="application/ld+json">
            {JSON.stringify({
              "@context": "https://schema.org",
              "@type": "FAQPage",
              "mainEntity": siteData.faqs.map(faq => ({
                "@type": "Question",
                "name": faq.question,
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": decodeHtml(faq.answer).replace(/<[^>]*>/g, '') // HTML taglarını temizle
                }
              }))
            })}
          </script>
        )}
        
        <html lang="tr" />
        
      </Helmet>
      {/* Header */}

      <section className="sliderarea">
         {/* Swiper Slider Bölümü */}
         {siteData.sliders && siteData.sliders.length > 0 && (
          <section>
            <Swiper
              modules={[Navigation, Pagination, Autoplay, EffectFade]}
              spaceBetween={0}
              slidesPerView={1}
              navigation={true}
              pagination={{ clickable: true }}
              autoplay={{
                delay: 10000,
                disableOnInteraction: false,
              }}
              effect="fade"
              fadeEffect={{
                crossFade: true
              }}
              loop={true}
              style={{
                '--swiper-navigation-color': '#fff',
                '--swiper-pagination-color': '#fff',
              }}
            >
              {siteData.sliders.map((slide, index) => (
                <SwiperSlide key={slide.id}>
                  <div className='slideritem'>
                    <Link className='headerlogos' to="/">
                      <img className='img-fluid desktopslideitem' src={slide.image_desktop} alt={slide.title} fetchpriority="high" />
                      <img className='img-fluid mobileslideitem' src={slide.image_mobile} alt={slide.title} fetchpriority="high" />
                    </Link>
                  </div>
                </SwiperSlide>
              ))}
            </Swiper>
          </section>
        )}
      </section>

      <section className='homegreenarea' ref={counterRef}>
        <div className='container'>
          <div className='homegreenarea_content text-center'>
            <h1>{siteData.siteSettings.map_title}</h1>
            {mapDescriptionHtml ? (
              <p
                dangerouslySetInnerHTML={{ __html: mapDescriptionHtml }}
              />
            ) : null}
          </div>
          <div className="turkey_map_area">
            <video 
              className='img-fluid' 
              autoPlay 
              muted 
              playsInline
              preload="metadata"
              style={{ width: '100%', height: 'auto' }}
            >
              <source src="/videos/harita_video3.mp4" type="video/mp4" />
            
            </video>
            <img src="/images/harita_video_kuslar.gif" className='img-fluid harita_birds' alt="Türkiye haritası üzerinde uçan kuşlar animasyonu" loading="lazy" />
            <img src="/images/haritaseffaf.webp" className='img-fluid harita_mask' alt="Türkiye haritası" loading="lazy" />
          </div>
          <div className='row'>
            {Object.keys(counterData).map((key) => (
              <div key={key} className='col-md-4'>
                <div className='homecounter'>
                  <span>
                    {counterData[key].prefix}{counters[key]}{counterData[key].suffix}
                  </span>
                  {counterData[key].isHtml ? (
                    <p dangerouslySetInnerHTML={{ __html: counterData[key].label }} />
                  ) : (
                    <p>{counterData[key].label}</p>
                  )}
                </div>
              </div>
            ))}
          </div>
          
        </div>
      </section>
      <div className='homegreenareasub'></div>
     

      <section className='home_tree_counter_area'>
            <div className='container'>
              <img src="/images/hm_tree_multiple_wave.svg" className='img-fluid hm_swiper_tree_img tf' alt="Ağaç ikon" loading="lazy" />
              <img src="/images/hm_tree_multiple_wave.svg" className='img-fluid hm_swiper_tree_img tf' alt="Ağaç ikon" loading="lazy" />
              <div className='hm_green_tree_subarea'>
                <h2 className='tree_subarea_title'>
                {siteData.siteSettings.text_tree_title}
                </h2>
                <div className='tree_subarea_navigation'>
                  <div className='swiper-button-prev tree-swiper-prev'></div>
                  <div className='swiper-button-next tree-swiper-next'></div>
                </div>
              </div>
              <div className='swiperarea'>
                <Swiper
                  modules={[Navigation]}
                  spaceBetween={0}
                  slidesPerView={5}
                  loop={false}
                  navigation={{
                    nextEl: '.tree-swiper-next',
                    prevEl: '.tree-swiper-prev',
                  }}
                  pagination={{ clickable: true }}
                  breakpoints={{
                    320: {
                      slidesPerView: 2,
                      spaceBetween: 0
                    },
                    768: {
                      slidesPerView: 3,
                      spaceBetween: 0
                    },
                    1024: {
                      slidesPerView: 4,
                      spaceBetween: 0
                    },
                    1200: {
                      slidesPerView: 5,
                      spaceBetween: 0
                    }
                  }}
                  style={{
                    '--swiper-navigation-color': '#275243',
                    '--swiper-pagination-color': '#275243',
                  }}
                >
                
                  {siteData?.trees && siteData.trees.length > 0 ? siteData.trees.map((tree, index) => (
                    <SwiperSlide key={index}>
                      <div className='swiper-item'>
                        <div className='swiper-item-content tree_month_content'>
                          <div className='tree_month_name' style={{background: tree.color}}>
                            <span>{tree.year}</span>
                            {tree.month}
                          </div>
                          <div className='tree_month_tree_count'>
                            {tree.count === '-' ? '-' : formatNumber(parseInt(tree.count))}
                            <span>AĞAÇ</span>
                          </div>
                        </div>
                      </div>
                    </SwiperSlide>
                  )) : (
                    <SwiperSlide>
                      <div className='swiper-item'>
                        <div className='swiper-item-content tree_month_content'>
                          <div className='tree_month_name'>
                            <span>Veri</span>
                            YÜKLENİYOR
                          </div>
                          <div className='tree_month_tree_count'>
                            -
                            <span>AĞAÇ</span>
                          </div>
                        </div>
                      </div>
                    </SwiperSlide>
                  )}
         
                 
                </Swiper>
              </div>
              <div className='green_wavehm_1'></div>
            </div>
      </section>

      <section className='hm_goal_area'>
        <div className='container'>
          <div className='row'>
            <div className='col-md-5'>
              <h2>{siteData.siteSettings.purpose_mini_head}</h2>
              <h3>{siteData.siteSettings.purpose_head}</h3>
              <div dangerouslySetInnerHTML={{ __html: decodeHtml(siteData.siteSettings.purpose_description) }} />
            </div>
            <div className='col-md-7'>
              
              <img src="/images/image_metin_optimized.webp" className='img-fluid hm_goal_area_img' alt="Toprakta fidan diken iki kişi" loading="lazy" />
            </div>
          </div>
        </div>
      </section>


      <section className='hm_goal_area_two'>
        <div className='container'>
          <div className='row justify-content-center'>
            <div className='col-12 text-center'>
              <h2 className='goals-title'>{siteData.siteSettings.text_goals_title}</h2>
            </div>
          </div>
          
          <div className='row goals-grid'>
            {siteData?.goals && siteData.goals.length > 0 ? siteData.goals.map((goal, index) => (
              <div key={index} className='col-lg-6 col-md-6 mb-4'>
                <div className='goal-card'>
                  <div className='row align-items-center'>
                    <div className='col-md-3 text-center'>
                      <div className='goal-icon'>
                        <img src={goal.icon} className='img-fluid' alt={`${goal.title} ikon`} loading="lazy" />
                      </div>
                    </div>
                    <div className='col-md-9'>
                      <h3 className='goal-card-title'>{goal.title}</h3>
                      <p className='goal-card-text'>
                        {goal.description}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            )) : (
              <div className='col-12'>
                <p>Hedefler yükleniyor...</p>
              </div>
            )}
          </div>
        </div>
      </section>

      <section>
        <div className='container'>
          <div className='row'>
            {siteData?.videos && siteData.videos.length > 0 ? siteData.videos.map((video, index) => (
              <div key={index} className='col-md-6'>
                <div className='hm_video_card'>
                  <div className='hm_video_card_img' onClick={() => openVideo(video.video)} style={{ cursor: 'pointer' }}>
                    <img src="/images/play_icon.svg" className='img-fluid hm_card_play_icon' alt="Play Icon" loading="lazy" />
                    <img src={video.cover} className='img-fluid hm_card_gif' alt="Home Video Card Image" loading="lazy" />
                  </div>
                  <div className='hm_video_card_content'>
                    <h2 style={{display: 'none'}}>Video</h2>
                    <p>{video.description}</p>
                    <button onClick={() => openVideo(video.video)} className='hm_video_card_btn' style={{ border: 'none', cursor: 'pointer' }}>{siteData.siteSettings.text_watch_now}</button>
                  </div>
                </div>
              </div>
            )) : (
              <div className='col-12'>
                <p>Videolar yükleniyor...</p>
              </div>
            )}
          </div>
        </div>
      </section>

      <section className='project_gallery'> 
        <div className='project_gallery_in' style={{background: 'url(/images/green_tree_animals.svg) top center repeat-x'}}>
        </div>
        <div className='project_gallery_content'>
          <div className='container-fluid'>
         <div className='row'>
          <div className='col-6c'> <h2>{siteData.siteSettings.text_gallery_title}</h2></div>
          <div className='col-6c text-end' style={{position: 'relative', top: '10px'}}>
            <div className='gallery_navigation'>
              <div className='swiper-button-prev gallery-swiper-prev'></div>
              <div className='swiper-button-next gallery-swiper-next'></div>
            </div>
          </div>
         </div>
                     <div className='project_gallery_images'>
             <Swiper
               modules={[Navigation]}
               spaceBetween={20}
               slidesPerView={4}
               loop={true}
               navigation={{
                 nextEl: '.gallery-swiper-next',
                 prevEl: '.gallery-swiper-prev',
               }}
               breakpoints={{
                 320: {
                   slidesPerView: 1,
                   spaceBetween: 10
                 },
                 768: {
                   slidesPerView: 2,
                   spaceBetween: 15
                 },
                 1024: {
                   slidesPerView: 3,
                   spaceBetween: 20
                 },
                 1200: {
                   slidesPerView: 4,
                   spaceBetween: 20
                 }
               }}
               style={{
                 '--swiper-navigation-color': '#275243',
                 '--swiper-pagination-color': '#275243',
               }}
             >
            
              {siteData?.gallery && siteData.gallery.length > 0 ? siteData.gallery.map((item, index) => (
                <SwiperSlide key={index}>
                  <div className='project-gallery-item'>
                    <div className='project-gallery-item-content'>
                      <img 
                        src={item.cover} 
                        className='img-fluid' 
                        alt={`${item.title} resmi`} 
                        data-fancybox="gallery"
                        data-src={item.cover}
                        data-caption={`<strong>${item.title}</strong><br/>${item.description}`}
                        style={{ cursor: 'pointer' }}
                        loading="lazy"
                      />
                      <div className='project-gallery-item-text'>
                        <h3><img src="/images/location.svg" className='img-fluid' alt="Project Gallery Image" loading="lazy" /> {item.title}</h3>
                        <p>{item.description}</p>
                      </div>
                    </div>
                  </div>
                </SwiperSlide>
              )) : (
                <SwiperSlide>
                  <div className='project-gallery-item'>
                    <div className='project-gallery-item-content'>
                      <div className='project-gallery-item-text'>
                        <h3>Destekçiler yükleniyor...</h3>
                      </div>
                    </div>
                  </div>
                </SwiperSlide>
              )}

             </Swiper>
             <div className='project_gallery_social text-center'>
              <div className='project_gallery_social_item type_text'>
                <a href="#." className='project_gallery_social_item_text'>
                {siteData.siteSettings.text_follow_us}
                </a>
              </div>
                             <div className='project_gallery_social_item type_icon'>
                 <a href="https://www.instagram.com/aljturkiye" target="_blank" rel="noopener noreferrer">
                   <img src="/images/icon_instagram.svg" className='img-fluid' alt="Project Gallery Social Image" loading="lazy" />
                 </a>
               </div>
               
               <div className='project_gallery_social_item type_icon'>
                 <a href="https://www.facebook.com/share/1Nu4ppKP1s/?mibextid=wwXIfr" target="_blank" rel="noopener noreferrer">
                   <img src="/images/icon_facebook.svg" className='img-fluid' alt="Project Gallery Social Image" loading="lazy" />
                 </a>
               </div>
               
               <div className='project_gallery_social_item type_icon'>
                 <a href="https://www.linkedin.com/company/aljt%C3%BCrkiye/" target="_blank" rel="noopener noreferrer">
                   <img src="/images/icon_linkedin.svg" className='img-fluid' alt="Project Gallery Social Image" loading="lazy" />
                 </a>
               </div>
              
             </div>
           </div>
           
          </div>
          <div className='dark_wave_bg_' ></div>
        </div>
       
       
      </section>

             <section className='hm_faq'>
                <div className='container'>
                 <h2>{siteData.siteSettings.text_faq_title}</h2>
                 <div className='hm_faq_content'>
                   <div className='row'>
                     {siteData.faqs && siteData.faqs.length > 0 ? (
                       <>
                         {/* Sol Kolon - İlk Yarı */}
                         <div className='col-md-6'>
                           {siteData.faqs.slice(0, Math.ceil(siteData.faqs.length / 2)).map((faq, index) => (
                             <div key={faq.id} className='faq-item'>
                               <div className='faq-question' onClick={() => toggleFaq(index)}>
                                 <div className='faq-icon'>
                                   <span className={activeFaq === index ? 'faq-minus' : 'faq-plus'}>
                                     +
                                   </span>
                                 </div>
                                 <h3>{faq.question}</h3>
                               </div>
                               <div className={`faq-answer ${activeFaq === index ? 'active' : ''}`}>
                                 <div dangerouslySetInnerHTML={{ __html: decodeHtml(faq.answer) }} />
                               </div>
                               <hr></hr> 
                             </div>
                           ))}
                         
                         </div>
                         
                         {/* Sağ Kolon - İkinci Yarı */}
                         <div className='col-md-6'>
                           {siteData.faqs.slice(Math.ceil(siteData.faqs.length / 2)).map((faq, index) => {
                             const adjustedIndex = index + Math.ceil(siteData.faqs.length / 2);
                             return (
                               <div key={faq.id} className='faq-item'>
                                 <div className='faq-question' onClick={() => toggleFaq(adjustedIndex)}>
                                   <div className='faq-icon'>
                                     <span className={activeFaq === adjustedIndex ? 'faq-minus' : 'faq-plus'}>
                                       +
                                     </span>
                                   </div>
                                   <h3>{faq.question}</h3>
                                 </div>
                                 <div className={`faq-answer ${activeFaq === adjustedIndex ? 'active' : ''}`}>
                                   <div dangerouslySetInnerHTML={{ __html: decodeHtml(faq.answer) }} />
                                 </div>
                                 <hr></hr> 
                               </div>
                             );
                           })}
                          
                         </div>
                       </>
                     ) : (
                       <div className='col-12'>
                         <p>FAQ verileri yükleniyor...</p>
                       </div>
                     )}
                   </div>
                 </div>
                </div>
        </section>
        <div className='footer_wave_bg_'>x</div>

        {/* Footer */}
        <Footer siteData={siteData} />

    </div>
  );
}

export default Home;
